import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import API from "../Context/api";
import EditReceiptForm from "./EditReceiptForm";
import AddReceiptForm from "./AddReceiptForm";
import { useAuth } from "../Context/AuthContext";

function Receipts() {
  const [receipts, setReceipts] = useState([]);
  const [receiptToDelete, setReceiptToDelete] = useState(null);
  const [receiptToEdit, setReceiptToEdit] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalOpenEdit, setModalOpenEdit] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false); // State for deleting status

  const navigate = useNavigate();

  const { user } = useAuth();
  const isAdmin = user?.role === "Admin";

  const fetchReceipts = async () => {
    try {
      const response = await API.get("/receipts");
      setReceipts(response.data);
      console.log(response.data);
      setLoading(false);
    } catch (error) {
      setError("Failed to fetch receipts");
      setLoading(false);
      // console.error("Error fetching receipts:", error);
    }
  };

  useEffect(() => {
    fetchReceipts();
  }, []);

  const handleDeleteClick = (id) => {
    setReceiptToDelete(id);
    setModalOpen(true);
  };
  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-gray-500 text-lg animate-pulse">Loading...</div>
      </div>
    );
  }

  if (error)
    return <div className="text-center text-red-500 py-10">{error}</div>;

  const closeModal = () => {
    setModalOpen(false);
    setReceiptToDelete(null);
    setIsDeleting(false); // Reset deleting state
  };

  const handleDeleteReceipt = async () => {
    if (receiptToDelete) {
      setIsDeleting(true); // Set deleting state to true
      try {
        await API.delete(`/receipts/${receiptToDelete}`);
        fetchReceipts(); // Refresh the list immediately after deletion
      } catch (error) {
        console.error("Error deleting receipt:", error);
        alert("Failed to delete receipt.");
      } finally {
        closeModal();
      }
    }
  };

  const handleEditReceipt = (id) => {
    setReceiptToEdit(id);
    setModalOpenEdit(true);
  };

  const handleViewReceipt = (id) => {
    navigate(`/receipt/${id}`);
  };

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
  };

  const filteredReceipts = receipts.filter((receipt) =>
    (receipt.receiptDetails?.client || "")
      .toLowerCase()
      .includes(searchQuery.toLowerCase())
  );

  const handleAddReceipt = () => {
    setIsModalOpen(true);
  };

  const handleReceiptUpdated = () => {
    fetchReceipts();
    setIsModalOpen(false);
    setModalOpenEdit(false);
  };

  return (
    <div className="h-full flex flex-col">
      {/* Static Header */}
      <h3 className="text-xl font-semibold mb-2 text-gray-900">Receipts</h3>

      <div className="py-2 mb-4 flex items-center justify-between gap-4 flex-wrap">
        <input
          type="text"
          placeholder="Search by customer..."
          value={searchQuery}
          onChange={handleSearchChange}
          className="p-2 border rounded w-full md:w-64 focus:ring-2 focus:ring-indigo-500 focus:outline-none"
        />
        <button
          onClick={handleAddReceipt}
          className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 text-sm font-medium transition-all duration-200 shadow-sm w-full sm:w-auto"
        >
          Add Receipt
        </button>
      </div>

      {/* Scrollable Content */}
      <div className="flex-1 overflow-y-auto space-y-3">
        <div className="hidden sm:block bg-white rounded-lg shadow-sm">
          <table className="w-full text-left table-fixed">
            <thead className="bg-gray-100 sticky top-0 z-10">
              <tr>
                <th className="w-[12%] px-4 py-3 text-sm font-semibold text-gray-600 uppercase tracking-wider">
                  Receipt No
                </th>
                <th className="w-[12%] px-4 py-3 text-sm font-semibold text-gray-600 uppercase tracking-wider">
                  Customer
                </th>
                <th className="w-[12%] px-4 py-3 text-sm font-semibold text-gray-600 uppercase tracking-wider">
                  Date
                </th>
                <th className="w-[12%] px-4 py-3 text-sm font-semibold text-gray-600 uppercase tracking-wider">
                  Balance (UGX)
                </th>
                <th className="w-[12%] px-4 py-3 text-sm font-semibold text-gray-600 uppercase tracking-wider">
                  Total (UGX)
                </th>
                <th className="w-[15%] px-4 py-3 text-sm font-semibold text-gray-600 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-300">
              {filteredReceipts.map((receipt, index) => (
                <tr
                  key={receipt.id}
                  className={`text-sm ${
                    index % 2 === 0 ? "bg-white" : "bg-gray-50"
                  } hover:bg-gray-100 transition-colors duration-150`}
                >
                  <td className="px-4 py-3 text-gray-700 text-sm whitespace-normal break-words align-top">
                    <span
                      onClick={() => handleViewReceipt(receipt.id)}
                      className="hover:underline cursor-pointer"
                    >
                      {receipt.receiptDetails.receiptNo}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-gray-700 text-sm whitespace-normal break-words align-top">
                    {receipt.receiptDetails.client}
                  </td>
                  <td className="px-4 py-3 text-gray-700 text-sm whitespace-normal break-words align-top">
                    {new Date(receipt.receiptDetails.date).toLocaleDateString()}
                  </td>
                  <td className="px-4 py-3 text-gray-700 text-sm whitespace-normal break-words align-top">
                    {receipt.receiptDetails.balance.toLocaleString()}
                  </td>
                  <td className="px-4 py-3 text-gray-700 text-sm whitespace-normal break-words align-top">
                    {receipt.receiptDetails.totalAmount.toLocaleString()}
                  </td>
                  <td className="px-4 py-3 flex gap-2 flex-row justify-start items-center flex-wrap">
                    {/* <button
                      onClick={() => handleViewReceipt(receipt.id)}
                      className="bg-blue-500 text-white w-20 px-3 py-1 rounded-md hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:ring-opacity-50 transition-all duration-200"
                    >
                      View
                    </button> */}
                    <button
                      onClick={() => handleEditReceipt(receipt.id)}
                      className="bg-blue-500 text-white w-20 px-3 py-1 rounded-md hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:ring-opacity-50 transition-all duration-200"
                    >
                      Edit
                    </button>
                    {isAdmin && (
                      <button
                        onClick={() => handleDeleteClick(receipt.id)}
                        className="bg-red-500 text-white w-20 px-3 py-1 rounded-md hover:bg-red-600 focus:outline-none focus:ring-2 focus:ring-red-400 focus:ring-opacity-50 transition-all duration-200"
                      >
                        Delete
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Cards for Small Screens */}
        <div className="md:hidden space-y-4">
          {filteredReceipts.map((receipt) => (
            <div key={receipt.id} className="bg-white p-4 rounded-lg shadow-md">
              <h3 className="text-lg font-semibold text-gray-900 truncate">
                <span onClick={() => handleViewReceipt(receipt.id)}>
                  {receipt.receiptDetails.receiptNo}
                </span>
              </h3>
              <p className="text-sm text-gray-600 mt-1">
                {receipt.receiptDetails.client}
              </p>
              <p className="text-sm text-gray-500">
                Date:{" "}
                {new Date(receipt.receiptDetails.date).toLocaleDateString()}
              </p>
              <p className="text-sm text-gray-500">
                Balance: UGX {receipt.receiptDetails.balance.toLocaleString()}
              </p>
              <p className="text-sm text-gray-500">
                Total: UGX {receipt.receiptDetails.totalAmount.toLocaleString()}
              </p>
              <div className="mt-3 flex flex-wrap gap-2">
                {/* <button
                  onClick={() => handleViewReceipt(receipt.id)} // Changed to id
                  className="bg-blue-500 text-white px-3 py-1 rounded-md hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:ring-opacity-50 transition-all duration-200"
                >
                  View
                </button> */}
                <button
                  onClick={() => handleEditReceipt(receipt.id)} // Changed to id
                  className="bg-blue-500 text-white px-3 py-1 rounded-md hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:ring-opacity-50 transition-all duration-200"
                >
                  Edit
                </button>
                {isAdmin && (
                  <>
                    <button
                      onClick={() => handleDeleteClick(receipt.id)} // Changed to id
                      className="bg-red-500 text-white px-3 py-1 rounded-md hover:bg-red-600 focus:outline-none focus:ring-2 focus:ring-red-400 focus:ring-opacity-50 transition-all duration-200"
                    >
                      Delete
                    </button>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
      {/* Delete Confirmation Modal */}
      {modalOpen && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-60 z-50 px-4">
          <div className="bg-white p-6 rounded-lg shadow-xl w-full max-w-md">
            <h2 className="text-xl font-bold text-gray-900">
              Confirm Deletion
            </h2>
            <p className="text-gray-600 mt-2">
              Are you sure you want to delete this receipt?
            </p>
            <div className="mt-6 flex justify-end gap-3">
              <button
                onClick={closeModal}
                className="bg-gray-500 text-white px-4 py-2 rounded-md hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-400 focus:ring-opacity-50 transition-all duration-200"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteReceipt}
                disabled={isDeleting}
                className="bg-red-500 text-white px-4 py-2 rounded-md hover:bg-red-600 focus:outline-none focus:ring-2 focus:ring-red-400 focus:ring-opacity-50 transition-all duration-200"
              >
                {isDeleting ? "Deleting..." : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Receipt Modal */}
      {isModalOpen && (
        <AddReceiptForm
          setIsModalOpen={setIsModalOpen}
          onReceiptUpdated={handleReceiptUpdated}
        />
      )}

      {/* Edit Receipt Modal */}
      {modalOpenEdit && (
        <EditReceiptForm
          id={receiptToEdit}
          setIsModalOpen={setModalOpenEdit}
          onReceiptUpdated={handleReceiptUpdated}
        />
      )}
    </div>
  );
}

export default Receipts;
